import {Column, Header, Row, Table} from "components/card-table"
import React, {memo, useMemo} from "react"
import {shapeComponent, ShapeComponent} from "set-state-compare/build/shape-component"
import Button from "components/chalky-layout/button"
import DaysSinceLastActivity from "./days-since-last-activity"
import moment from "moment"
import OptionsButton from "components/school-roles/user-circle/options-button"
import PropTypes from "prop-types"
import propTypesExact from "prop-types-exact"
import RoleBadge from "./role-badge"
import {School} from "models"
import SchoolRolesNewRoleModal from "components/school-roles/new-role-modal"
import SortLink from "./sort-link"
import Text from "@kaspernj/api-maker/build/utils/text"
import useI18n from "i18n-on-steroids/build/src/use-i18n.js"
import {View} from "react-native"

export default memo(shapeComponent(class RoutesUserMyUsersUsersTable extends ShapeComponent {
  static propTypes = propTypesExact({
    params: PropTypes.object,
    school: PropTypes.instanceOf(School).isRequired,
    schoolRoles: PropTypes.object.isRequired,
    schoolClassesForSchoolsAndPeople: PropTypes.object.isRequired,
    schoolClassInvitationsByUserId: PropTypes.object.isRequired
  })

  now = new Date()

  setup() {
    const {t} = useI18n({namespace: "js.routes.user.my_users.users_table"})
    const {params, schoolRoles, schoolClassesForSchoolsAndPeople} = this.p
    const sort = params?.sort || "name"
    const sortMode = params?.sortMode

    this.orderedSchoolRoles = useMemo(() => {
      let sortValueBefore = -1
      let sortValueAfter = 1

      if (sortMode == "desc") {
        sortValueBefore = 1
        sortValueAfter = -1
      }

      return Object.values(schoolRoles).sort((contactRelationship1, contactRelationship2) => {
        if (sort == "name" && sortMode != "desc") {
          return contactRelationship1.contact.name()?.toLowerCase()?.localeCompare(contactRelationship2.contact.name()?.toLowerCase())
        } else if (sort == "name" && sortMode == "desc") {
          return contactRelationship2.contact.name()?.toLowerCase()?.localeCompare(contactRelationship1.contact.name()?.toLowerCase())
        } else if (sort == "classes") {
          const classes1 = schoolClassesForSchoolsAndPeople[contactRelationship1.contact.id()]?.length || 0
          const classes2 = schoolClassesForSchoolsAndPeople[contactRelationship2.contact.id()]?.length || 0

          if (sortMode == "desc") {
            return classes2 - classes1
          } else {
            return classes1 - classes2
          }
        } else if (sort == "days_since_latest_activity") {
          const days1 = this.daysSinceLatestActivity(contactRelationship1)
          const days2 = this.daysSinceLatestActivity(contactRelationship2)

          if (days1 === null && days2 !== null) {
            return sortValueBefore
          } else if (days1 !== null && days2 === null) {
            return sortValueAfter
          } else if (days1 === null && days2 === null) {
            return 0
          } else if (sortMode == "desc") {
            return days2 - days1
          } else {
            return days1 - days2
          }
        } else if (sort == "role") {
          const roles1 = contactRelationship1.schoolRoles.map((schoolRole) => schoolRole.contactRelationshipType().name().toLowerCase()).sort().join("--")
          const roles2 = contactRelationship2.schoolRoles.map((schoolRole) => schoolRole.contactRelationshipType().name().toLowerCase()).sort().join("--")

          if (sortMode == "desc") {
            return roles2.localeCompare(roles1)
          } else {
            return roles1.localeCompare(roles2)
          }
        } else if (sort == "school") {
          // Ignore - school always the same in tables
        } else {
          throw new Error(`Unknown sort: ${sort}`)
        }

        return 0
      })
    }, [schoolRoles, sort, sortMode])

    this.setInstance({t})
    this.useStates({newRoleModal: false})
  }

  render() {
    const {orderedSchoolRoles, t} = this.tt
    const {params, school, schoolRoles, schoolClassesForSchoolsAndPeople} = this.p

    return (
      <View>
        {this.s.newRoleModal &&
          <SchoolRolesNewRoleModal onRequestClose={this.tt.onNewRoleModalRequestClose} school={school} />
        }
        <View style={this.schoolNameViewStyle ||= {flexDirection: "row", alignItems: "center"}}>
          <Text style={this.usersTableHeadlineTextStyle ||= {marginRight: 24, color: "#2c2c96", fontSize: 22, fontWeight: "bold"}}>
            {school.name()}
          </Text>
          <Button icon="plus" iconPosition="left" onPress={this.tt.onInviteUserPress} primary size="extra-small" title={t(".invite_user")} />
        </View>
        <Table dataSet={this.cache("tableDataSet", {schoolId: school.id()}, [school.id()])}>
          <Row>
            <Header>
              <SortLink identifier="name" label={t(".name")} params={params} school={school} />
            </Header>
            <Header>
              <SortLink identifier="school" label={t(".school")} params={params} school={school} />
            </Header>
            <Header width={200}>
              <SortLink identifier="role" label={t(".user_role")} params={params} school={school} />
            </Header>
            <Header width={90}>
              <SortLink identifier="classes" label={t(".classes")} params={params} school={school} />
            </Header>
            <Header width={220}>
              <SortLink identifier="days_since_latest_activity" label={t(".days_since_latest_activity")} params={params} school={school} />
            </Header>
            <Header dataSet={this.actionsHeaderDataSet ||= {class: "actions-header"}} width={50} />
          </Row>
          {orderedSchoolRoles.map((schoolRole) =>
            <Row key={schoolRole.contact.id()}>
              <Column style={this.nameEmailAndPendingColumnStyle ||= {justifyContent: "center"}}>
                <Text style={this.nameTextStyle ||= {color: "#2c2c96", fontSize: 16}}>
                  {schoolRole.contact.name()}
                </Text>
                <View style={this.emailAndPendingViewStyle ||= {flexDirection: "row", alignItems: "center"}}>
                  <Text style={this.emailTextStyle ||= {color: "#87a2d5", fontSize: 12}}>
                    {schoolRole.contact.email()}
                  </Text>
                  {this.hasPendingInvite(schoolRole) &&
                    <View
                      style={this.pendingBadgeStyle ||= {
                        backgroundColor: "#e5dfff",
                        marginLeft: 10,
                        paddingHorizontal: 6,
                        paddingVertical: 1,
                        borderRadius: 22
                      }}
                    >
                      <Text style={this.pendingTextStyle ||= {color: "#1b1f72", fontSize: 12, fontWeight: "bold"}}>
                        {t(".pending_invite")}
                      </Text>
                    </View>
                  }
                </View>
              </Column>
              <Column style={this.schoolColumnStyle ||= {justifyContent: "center"}}>
                <Text>
                  {school.name()}
                </Text>
              </Column>
              <Column style={this.rolesColumnStyle ||= {flexDirection: "row", alignItems: "center"}}>
                <View style={{flexDirection: "row", flexWrap: "wrap", maxWidth: "100%"}}>
                  {schoolRole.schoolRoles.map((schoolRole) =>
                    <View key={schoolRole.id()} style={{alignItems: "center", justifyContent: "center", marginTop: 5, marginRight: 5, marginBottom: 5}}>
                      <RoleBadge schoolRole={schoolRole} />
                    </View>
                  )}
                </View>
              </Column>
              <Column style={this.classesColumnStyle ||= {justifyContent: "center"}}>
                <Text>
                  {schoolClassesForSchoolsAndPeople[schoolRole.contact.id()]?.length || 0}
                </Text>
              </Column>
              <Column style={this.daysSinceLastActivityColumnStyle ||= {justifyContent: "center"}}>
                {schoolRole.contact.user() &&
                  <DaysSinceLastActivity days={this.daysSinceLatestActivity(schoolRole)} />
                }
              </Column>
              <Column style={this.actionsColumnStyle ||= {justifyContent: "center"}}>
                {schoolRole.schoolRoles.map((schoolRole) =>
                  <OptionsButton key={schoolRole.id()} schoolRole={schoolRole} />
                )}
              </Column>
            </Row>
          )}
          {schoolRoles.length === 0 &&
            <Row>
              <Column>
                <Text>
                  {t(".no_school_roles_found")}
                </Text>
              </Column>
            </Row>
          }
        </Table>
      </View>
    )
  }

  daysSinceLatestActivity(schoolRole) {
    if (!schoolRole.contact.user()) return null

    return moment(this.now).diff(schoolRole.contact.user().lastActivityAt(), "days") || 0
  }

  hasPendingInvite(schoolRole) {
    if (!schoolRole.contact.user()) {
      return false
    }

    const schoolClassInvitation = this.p.schoolClassInvitationsByUserId[schoolRole.contact.user().id()]

    if (schoolClassInvitation?.state() == "new") {
      return true
    }

    return false
  }

  onInviteUserPress = () => this.setState({newRoleModal: true})
  onNewRoleModalRequestClose = () => this.setState({newRoleModal: false})
}))
